// Types for DRQ queries based on drq.py structure
import { FilterInstance } from "../hooks/use-object-list-filters";
import { parseRelativeDate, parseRelativeDateWithTemporal } from "../lib/utils";

export interface DRQQuery {
    _query_class: string;
}

export interface DRQFilter extends DRQQuery {
    _query_class: "filter";
    _condition: string;
    _value: any;
    _type?: "text" | "select" | "checkbox" | "combobox" | "date-range" | "datetime-range";
    _field_name?: string; // Store original field name for reconstruction
}

export interface DRQExclude extends DRQQuery {
    _query_class: "exclude";
    _condition: string;
    _value: any;
    _type?: "text" | "select" | "checkbox" | "combobox" | "date-range" | "datetime-range";
    _field_name?: string;
}

export interface DRQCompositeQuery extends DRQQuery {
    _query_class: "compositequery";
    _sub_queries: DRQQuery[];
}

export interface DRQOrQuery extends DRQQuery {
    _query_class: "orquery";
    _sub_queries: DRQQuery[];
}

export interface DRQOrderBy extends DRQQuery {
    _query_class: "orderby";
    _order_by: string[];
}

export type AnyDRQQuery = DRQFilter | DRQExclude | DRQCompositeQuery | DRQOrQuery | DRQOrderBy;

// Simplified filter interface for building DRQ queries
export interface DRQFilterInput {
    field_name: string;
    type?: "text" | "select" | "checkbox" | "combobox" | "date-range" | "datetime-range";
    value: string;
    operator: string;
}

/** When `_type` is missing on a stored filter, infer from Django lookup so API conversion expands "today" etc. */
function drqFilterTypeForApi(condition: string | undefined, explicit?: string): DRQFilterInput["type"] | undefined {
    if (explicit) return explicit as DRQFilterInput["type"];
    const lookup = condition?.split("__").pop();
    if (lookup && ["range", "date", "year", "month", "week"].includes(lookup)) return "datetime-range";
    return undefined;
}

// Utility functions for building DRQ queries
export class DRQBuilder {
    /**
     * Format a Date instance to YYYY-MM-DD (no time component)
     */
    static formatDateYYYYMMDD(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }
    /**
     * Create a filter query
     */
    static filter(condition: string, value: any, type?: string, fieldName?: string): DRQFilter {
        return {
            _query_class: "filter",
            _condition: condition,
            _value: value,
            _type: type as any,
            _field_name: fieldName
        };
    }

    /**
     * Create an exclude query
     */
    static exclude(condition: string, value: any, type?: string, fieldName?: string): DRQExclude {
        return {
            _query_class: "exclude",
            _condition: condition,
            _value: value,
            _type: type as any,
            _field_name: fieldName
        };
    }

    /**
     * Create a composite query (AND logic)
     */
    static and(queries: AnyDRQQuery[]): DRQCompositeQuery | null {
        if (queries.length === 0) return null;
        if (queries.length === 1) return queries[0] as any;

        return {
            _query_class: "compositequery",
            _sub_queries: queries
        };
    }

    /**
     * Create an OR query
     */
    static or(queries: AnyDRQQuery[]): DRQOrQuery | null {
        if (queries.length === 0) return null;
        if (queries.length === 1) return queries[0] as any;

        return {
            _query_class: "orquery",
            _sub_queries: queries
        };
    }

    /**
     * Create an order by query
     */
    static orderBy(fields: string[]): DRQOrderBy {
        return {
            _query_class: "orderby",
            _order_by: fields
        };
    }

    /**
     * Convert a filter operator to Django field lookup
     */
    static getConditionFromOperator(fieldName: string, operator: string, value?: string | any, filterType?: string): string {
        // For exclude operations, use the base operator
        const baseOperator = DRQBuilder.shouldUseExclude(operator) ? DRQBuilder.getBaseOperatorForExclude(operator) : operator;

        // Special handling for date/datetime ranges with array values - PRIORITY 1
        // Only use __range for equals operator, other operators should use their specific lookups
        if ((filterType === "date-range" || filterType === "datetime-range") && value && Array.isArray(value)) {
            if (baseOperator === "equals") {
                return `${fieldName}__range`;
            }
            // For other operators (gt, gte, lt, lte), we'll handle them in convertRelativeDateForAPI
            // and return single values, so we don't force __range here
        }

        // Special handling for datetime-range fields with equals operator - use __exact for single values with time, __range for arrays - PRIORITY 2
        if (filterType === "datetime-range" && baseOperator === "equals") {
            // For datetime-range, check if we have a single value with time specified
            if (value && typeof value === "string") {
                // Skip ISO datetime with timezone (they should use __range)
                if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
                    return `${fieldName}__range`;
                }

                // Check if the value has a time component in user format (HH:MM, not ISO timezone)
                // Look for HH:MM pattern that's not part of ISO datetime format with timezone
                const hasTimeComponent =
                    value.match(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}$/) ||
                    value.match(/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}$/) ||
                    value.match(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}$/) ||
                    value.match(/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}:\d{2}$/) ||
                    // Also check for simple HH:MM pattern in user formats
                    (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}/) && value.match(/\d{1,2}:\d{2}/)) ||
                    (value.match(/^\d{4}-\d{2}-\d{2}/) && value.match(/\d{1,2}:\d{2}/));
                if (hasTimeComponent) {
                    // If time is specified in user format, use __exact
                    return `${fieldName}__exact`;
                }
            }
            // For arrays or values without time, use __range
            return `${fieldName}__range`;
        }

        // Special handling for date-range fields with equals operator - use __iexact for single dates, __range for arrays - PRIORITY 2.5
        if (filterType === "date-range" && baseOperator === "equals") {
            // For date-range, use __iexact for single values, __range for arrays
            if (value && Array.isArray(value)) {
                return `${fieldName}__range`;
            } else {
                return `${fieldName}__iexact`;
            }
        }

        // Check if this is a temporal filter that should use specific Django lookups - PRIORITY 3
        if ((filterType === "date-range" || filterType === "datetime-range") && value && baseOperator === "equals") {
            // For temporal lookups, we can only use equals operator
            if (typeof value === "string") {
                // Skip temporal lookups for simple ISO dates (YYYY-MM-DD format)
                if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    // For simple ISO dates, let the default logic handle it
                    // This will use __iexact for date-range and __range for datetime-range
                } else {
                    const result = parseRelativeDateWithTemporal(value);
                    if (result?.temporal) {
                        // For specific formats like "2024-01", use temporal lookups
                        // For relative expressions like "this month", use __range for consistency
                        if (value.match(/^\d{4}-\d{2}$/) || value.match(/^\d{4}$/)) {
                            return `${fieldName}${result.temporal.lookup}`;
                        } else {
                            // For relative expressions, prioritize __range over temporal lookups
                            return `${fieldName}__range`;
                        }
                    }
                }
            }
        }

        switch (baseOperator) {
            case "contains":
                return `${fieldName}__icontains`;
            case "equals":
                // Use __exact for checkbox fields, __iexact for others
                return filterType === "checkbox" ? `${fieldName}__exact` : `${fieldName}__iexact`;
            case "not_equal_to":
                // Use __exact for checkbox fields, __iexact for others
                return filterType === "checkbox" ? `${fieldName}__exact` : `${fieldName}__iexact`;
            case "starts_with":
                return `${fieldName}__istartswith`;
            case "ends_with":
                return `${fieldName}__iendswith`;
            case "lt":
                return `${fieldName}__lt`;
            case "gt":
                return `${fieldName}__gt`;
            case "gte":
                return `${fieldName}__gte`;
            case "lte":
                return `${fieldName}__lte`;
            case "is_empty":
                return `${fieldName}__isnull`;
            default:
                // Use __exact for checkbox fields, __iexact for others
                return filterType === "checkbox" ? `${fieldName}__exact` : `${fieldName}__iexact`;
        }
    }

    /**
     * Convert a Django field lookup back to operator and field name
     * Ajout d'un paramètre filterType pour distinguer les dates des nombres
     */
    static getOperatorFromCondition(condition: string, _filterType?: string): { fieldName: string; operator: string } {
        if (condition.includes("__")) {
            const parts = condition.split("__");
            const fieldName = parts.slice(0, -1).join("__");
            const lookup = parts[parts.length - 1];

            const operatorMap: Record<string, string> = {
                icontains: "contains",
                iexact: "equals",
                exact: "equals", // Keep for backward compatibility and checkbox fields
                istartswith: "starts_with",
                iendswith: "ends_with",
                lt: "lt",
                gt: "gt",
                isnull: "is_empty", // For extraction purposes
                gte: "gte",
                lte: "lte",
                range: "equals", // For date/datetime ranges
                year: "equals", // Special temporal lookups
                month: "equals",
                date: "equals",
                week: "equals"
            };

            return {
                fieldName,
                operator: operatorMap[lookup] || "equals"
            };
        }

        return { fieldName: condition, operator: "equals" };
    }

    /** DRQ for HTTP `query=`: expand relative dates; re-parse stored JSON if filter state is empty. */
    static resolveDrqQueryForApi(
        drqQuery: AnyDRQQuery | {},
        filterInstances: FilterInstance[] | undefined,
        filterLogic?: string
    ): AnyDRQQuery | {} {
        if (filterInstances?.length) return DRQBuilder.buildFromFilterInstancesForAPIWithLogic(filterInstances, filterLogic);
        if (!drqQuery || typeof drqQuery !== "object" || !("_query_class" in drqQuery)) return {};
        const extracted = DRQBuilder.extractFilterInstancesFromDRQ(drqQuery);
        return extracted.length ? DRQBuilder.buildFromFilterInstancesForAPIWithLogic(extracted, filterLogic ?? "") : drqQuery;
    }

    /**
     * Parse user-friendly datetime formats to ISO format
     */
    static parseUserDatetime(value: string): string {
        // If it looks like a relative date expression, return as-is
        if (!value.match(/^\d{1,2}\/\d{1,2}\/\d{4}/) && !value.match(/^\d{4}-\d{2}-\d{2}/)) {
            return value;
        }

        // Parse DD/MM/YYYY HH:MM format
        const ddmmyyyyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
        if (ddmmyyyyMatch) {
            const [, day, month, year, hour = "00", minute = "00"] = ddmmyyyyMatch;
            const paddedDay = day.padStart(2, "0");
            const paddedMonth = month.padStart(2, "0");
            const paddedHour = hour.padStart(2, "0");
            const paddedMinute = minute.padStart(2, "0");
            return `${year}-${paddedMonth}-${paddedDay}T${paddedHour}:${paddedMinute}:00`;
        }

        // Parse YYYY-MM-DD HH:MM format (add time if missing)
        const yyyymmddMatch = value.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{1,2}):(\d{2}))?/);
        if (yyyymmddMatch) {
            const [, datePart, hour = "00", minute = "00"] = yyyymmddMatch;
            const paddedHour = hour.padStart(2, "0");
            const paddedMinute = minute.padStart(2, "0");
            return `${datePart}T${paddedHour}:${paddedMinute}:00`;
        }

        // If already in ISO format or unrecognized, return as-is
        return value;
    }

    /**
     * Parse user-friendly date formats to ISO date format
     */
    static parseUserDate(value: string): string {
        // If it looks like a relative date expression, return as-is
        if (!value.match(/^\d{1,2}\/\d{1,2}\/\d{4}/) && !value.match(/^\d{4}-\d{2}-\d{2}/)) {
            return value;
        }

        // Parse DD/MM/YYYY format
        const ddmmyyyyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            const paddedDay = day.padStart(2, "0");
            const paddedMonth = month.padStart(2, "0");
            return `${year}-${paddedMonth}-${paddedDay}`;
        }

        // If already in YYYY-MM-DD format or unrecognized, return as-is
        return value;
    }

    /**
     * Convert relative date expressions to appropriate values for API calls
     */
    static convertRelativeDateForAPI(value: string, operator: string, filterType?: string): any {
        // Correction : gestion explicite des booléens pour les checkbox
        if (filterType === "checkbox") {
            if (typeof value === "string") {
                if (value === "True" || value === "true" || value === "1") return true;
                if (value === "False" || value === "false" || value === "0") return false;
            }
            return !!value;
        }

        // Pour les dates/datetimes, si l'opérateur est equals et que la valeur est une période relative, retourner un range (tableau)
        if ((filterType === "date-range" || filterType === "datetime-range") && value && !value.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T?/)) {
            if (operator === "equals") {
                const parsedRange = parseRelativeDate(value);
                if (parsedRange && parsedRange.from && parsedRange.to && parsedRange.from.getTime() !== parsedRange.to.getTime()) {
                    // C'est une période, on doit faire un range
                    if (filterType === "date-range") {
                        // IMPORTANT: envoyer des dates sans heure pour les champs date
                        return [DRQBuilder.formatDateYYYYMMDD(parsedRange.from), DRQBuilder.formatDateYYYYMMDD(parsedRange.to)];
                    }
                    return [parsedRange.from.toISOString(), parsedRange.to.toISOString()];
                }
            }
        }

        // Handle datetime-range user formats - parse before checking for relative dates
        if (filterType === "datetime-range" && value) {
            // Single value - parse user format first
            const parsedValue = DRQBuilder.parseUserDatetime(value);
            // If it's now in ISO format, check if time was specified
            if (parsedValue.match(/^\d{4}-\d{2}-\d{2}T/)) {
                // Check if the original value had a time component in user format (HH:MM, not ISO timezone)
                const hadTimeInOriginal =
                    value.match(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}$/) ||
                    value.match(/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}$/) ||
                    value.match(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}$/) ||
                    value.match(/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}:\d{2}$/) ||
                    // Also check for simple HH:MM pattern in user formats
                    (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}/) && value.match(/\d{1,2}:\d{2}/)) ||
                    (value.match(/^\d{4}-\d{2}-\d{2}/) && value.match(/\d{1,2}:\d{2}/));

                if (operator === "equals") {
                    if (hadTimeInOriginal) {
                        // If time was specified, return exact value
                        return parsedValue;
                    } else {
                        // If no time was specified, create a range for the entire day
                        const datePart = parsedValue.split("T")[0];
                        const startOfDay = `${datePart}T00:00:00`;
                        const endOfDay = `${datePart}T23:59:59`;
                        return [startOfDay, endOfDay];
                    }
                }
                return parsedValue;
            }
            // Otherwise, continue with relative date processing
            value = parsedValue;
        }

        // Handle date-range user formats - parse before checking for relative dates
        if (filterType === "date-range" && value) {
            // Single value - parse user format first
            const parsedValue = DRQBuilder.parseUserDate(value);
            // If it's now in ISO format, return it directly
            if (parsedValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return parsedValue;
            }
            // Otherwise, continue with relative date processing
            value = parsedValue;
        }

        // Handle date/datetime relative values
        if ((filterType === "date-range" || filterType === "datetime-range") && value && !value.match(/^\d{4}-\d{2}-\d{2}T/)) {
            // Check if this should use a temporal lookup instead of a range
            const result = parseRelativeDateWithTemporal(value);
            if (result?.temporal && operator === "equals") {
                // For temporal lookups, we can only use equals operator
                if (result.temporal.lookup === "__year_month") {
                    // Special case: for year-month, we'll return the object for special handling
                    return result.temporal.value;
                }
                return result.temporal.value;
            }

            // For non-equals operators or when no temporal lookup is available, use range-based logic
            const parsedRange = parseRelativeDate(value);
            if (parsedRange) {
                switch (operator) {
                    case "after":
                        // For "after", use the end date of the range
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.to) : parsedRange.to.toISOString();
                    case "before":
                        // For "before", use the start date of the range
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.from) : parsedRange.from.toISOString();
                    case "gt":
                        // For "gt", use the end date of the range (greater than end = after the beginning of the period)
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.from) : parsedRange.from.toISOString();
                    case "gte":
                        // For "gte", use the start date of the range (greater than or equal to start = during or after)
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.from) : parsedRange.from.toISOString();
                    case "lt":
                        // For "lt", use the start date of the range (less than start = before the period)
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.from) : parsedRange.from.toISOString();
                    case "lte":
                        // For "lte", use the end date of the range (less than or equal to end = during or before the end of the period)
                        return filterType === "date-range" ? DRQBuilder.formatDateYYYYMMDD(parsedRange.from) : parsedRange.from.toISOString();
                    case "equals":
                    default:
                        // For "equals" with datetime-range, always return as range
                        if (filterType === "datetime-range") {
                            return [parsedRange.from.toISOString(), parsedRange.to.toISOString()];
                        }
                        // For "equals", use the start date (YYYY-MM-DD)
                        return DRQBuilder.formatDateYYYYMMDD(parsedRange.from);
                }
            }
        }

        // Convert to lowercase for case-insensitive operators
        if (typeof value === "string") {
            return value;
        }

        return value;
    }

    /**
     * Whether a string is a concrete date/datetime the API can accept (after conversion), not a leftover relative token.
     */
    static isApiSafeDateTimeString(s: string, filterType: "date-range" | "datetime-range"): boolean {
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            return true;
        }
        if (filterType === "datetime-range") {
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
                return true;
            }
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) {
                return true;
            }
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(s)) {
                return true;
            }
            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(s)) {
                return true;
            }
        }
        return false;
    }

    /**
     * True if convertRelativeDateForAPI produced something safe for Django date/datetime lookups (not an unparsed garbage string).
     */
    static isTemporalConversionApiSafe(result: unknown, filterType: "date-range" | "datetime-range"): boolean {
        if (result === null || result === undefined) {
            return false;
        }
        if (typeof result === "number" && Number.isFinite(result)) {
            return true;
        }
        if (typeof result === "object" && result !== null && "year" in result && "month" in result) {
            const o = result as { year: unknown; month: unknown };
            return typeof o.year === "number" && typeof o.month === "number";
        }
        if (Array.isArray(result)) {
            if (result.length !== 2) {
                return false;
            }
            return result.every(item => typeof item === "string" && DRQBuilder.isApiSafeDateTimeString(item, filterType));
        }
        if (typeof result === "string") {
            return DRQBuilder.isApiSafeDateTimeString(result, filterType);
        }
        return false;
    }

    /**
     * Two-part comma value for equals/not_equal_to (ISO range) as handled by buildFromFilterInputsForAPI.
     */
    static isCommaSeparatedIsoPairForTemporalFilter(parts: string[], filterType: "date-range" | "datetime-range", operator: string): boolean {
        if (parts.length !== 2 || (operator !== "equals" && operator !== "not_equal_to")) {
            return false;
        }
        if (filterType === "datetime-range") {
            return parts.every(
                date =>
                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(date) ||
                    /^\d{4}-\d{2}-\d{2}$/.test(date) ||
                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(date) ||
                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(date)
            );
        }
        if (filterType === "date-range") {
            return parts.every(d => /^\d{4}-\d{2}-\d{2}$/.test(d));
        }
        return false;
    }

    static validateSingleTemporalFilterPart(
        value: string,
        operator: string,
        filterType: "date-range" | "datetime-range"
    ): { valid: boolean; error?: string } {
        const converted = DRQBuilder.convertRelativeDateForAPI(value, operator, filterType);
        if (DRQBuilder.isTemporalConversionApiSafe(converted, filterType)) {
            return { valid: true };
        }
        return {
            valid: false,
            error: "Unrecognized date or relative expression. Use e.g. today, yesterday, last 7 days, 15/01/2024, or ISO dates."
        };
    }

    /**
     * Validates date-range / datetime-range filter input before applying search (avoids backend 500 on bad DRQ values).
     */
    static validateTemporalFilterInput(
        value: string,
        operator: string,
        filterType: "date-range" | "datetime-range"
    ): { valid: boolean; error?: string } {
        const trimmed = value.trim();

        if (operator === "is_empty" || operator === "is_not_empty") {
            return { valid: true };
        }

        if (!trimmed) {
            if (operator === "equals" || operator === "not_equal_to") {
                return { valid: true };
            }
            if (["gt", "gte", "lt", "lte"].includes(operator)) {
                return { valid: false, error: "Please enter a date value" };
            }
            return { valid: true };
        }

        if (trimmed.length === 1 && !/\d/.test(trimmed)) {
            return {
                valid: false,
                error: "Enter a full date or a supported relative expression (e.g. today, yesterday)."
            };
        }

        if (trimmed.includes(",")) {
            const parts = trimmed
                .split(",")
                .map(p => p.trim())
                .filter(p => p.length > 0);
            if (parts.length < 2) {
                return { valid: false, error: "Invalid comma-separated value" };
            }

            if (DRQBuilder.isCommaSeparatedIsoPairForTemporalFilter(parts, filterType, operator)) {
                return { valid: true };
            }

            const multiOps = ["equals", "not_equal_to", "contains", "not_contains", "starts_with", "ends_with"];
            if (multiOps.includes(operator)) {
                for (const part of parts) {
                    const inner = DRQBuilder.validateSingleTemporalFilterPart(part, operator, filterType);
                    if (!inner.valid) {
                        return { valid: false, error: inner.error };
                    }
                }
                return { valid: true };
            }
        }

        return DRQBuilder.validateSingleTemporalFilterPart(trimmed, operator, filterType);
    }

    /**
     * Convert filter value based on operator and filter type
     */
    static convertValue(value: string, _operator: string, filterType?: string): any {
        // Correction : gestion explicite des booléens pour les checkbox
        if (filterType === "checkbox") {
            if (typeof value === "string") {
                if (value === "True" || value === "true" || value === "1") return true;
                if (value === "False" || value === "false" || value === "0") return false;
            }
            return !!value;
        }

        // NOUVEAU : Pour date-range et datetime-range, si la valeur ne ressemble pas à une date ISO ou DD/MM/YYYY, on la garde telle quelle (relative)
        if ((filterType === "date-range" || filterType === "datetime-range") && value && !value.match(/^\d{4}-\d{2}-\d{2}/) && !value.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
            return value;
        }

        return value;
    }

    /**
     * Check if an operator should use exclude instead of filter
     */
    static shouldUseExclude(operator: string): boolean {
        return ["not_contains", "not_equal_to", "is_not_empty"].includes(operator);
    }

    /**
     * Get the base operator for exclude operations
     */
    static getBaseOperatorForExclude(operator: string): string {
        switch (operator) {
            case "not_contains":
                return "contains";
            case "not_equal_to":
                return "equals";
            case "is_not_empty":
                return "is_empty";
            default:
                return operator;
        }
    }

    /**
     * Build DRQ query from filter inputs for API calls (converts relative dates)
     */
    static buildFromFilterInputsForAPI(filterInputs: DRQFilterInput[]): AnyDRQQuery | null {
        const subQueries: AnyDRQQuery[] = [];

        filterInputs.forEach(input => {
            if (
                input.value ||
                input.operator === "is_empty" ||
                input.operator === "is_not_empty" ||
                ((input.operator === "equals" || input.operator === "not_equal_to") && (!input.value || input.value.trim() === ""))
            ) {
                if (input.operator === "is_empty") {
                    subQueries.push(DRQBuilder.filter(`${input.field_name}__isnull`, input.value === "True", input.type, input.field_name));
                    return;
                }
                // Handle datetime-range with comma-separated ISO dates (special case)
                if ((input.type === "date-range" || input.type === "datetime-range") && input.operator === "equals" && typeof input.value === "string" && input.value.includes(",")) {
                    const dates = input.value.split(",").map(date => date.trim());
                    // Check if all parts are valid ISO dates (including timezone)
                    const isValidISO = dates.every(
                        date =>
                            date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) ||
                            date.match(/^\d{4}-\d{2}-\d{2}$/) ||
                            date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/) ||
                            date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/)
                    );
                    if (isValidISO && dates.length === 2) {
                        const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, dates, input.type);
                        subQueries.push(DRQBuilder.filter(condition, dates, input.type, input.field_name));
                        return;
                    }
                }

                // Handle comma-separated values for contains, not_contains, equals, not_equal_to, starts_with, ends_with operators
                // Also handle datetime-range with comma-separated ISO dates
                if (
                    (input.operator === "contains" ||
                        input.operator === "not_contains" ||
                        input.operator === "equals" ||
                        input.operator === "not_equal_to" ||
                        input.operator === "starts_with" ||
                        input.operator === "ends_with") &&
                    typeof input.value === "string" &&
                    input.value.includes(",")
                ) {
                    const values = input.value
                        .split(",")
                        .map(v => v.trim())
                        .filter(v => v.length > 0);

                    // Special handling for not_equal_to: create separate exclude filters and combine with AND
                    if (input.operator === "not_equal_to") {
                        const queries: AnyDRQQuery[] = [];
                        values.forEach(val => {
                            const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                            const convertedValue = DRQBuilder.convertRelativeDateForAPI(val, input.operator, input.type);
                            queries.push(DRQBuilder.exclude(condition, convertedValue, input.type, input.field_name));
                        });
                        if (queries.length > 0) {
                            // Combine all exclude filters with AND (all must be excluded)
                            subQueries.push(DRQBuilder.and(queries) as AnyDRQQuery);
                        }
                        return;
                    }

                    // Special handling for not_contains: create separate exclude filters and combine with AND
                    if (input.operator === "not_contains") {
                        const queries: AnyDRQQuery[] = [];
                        values.forEach(val => {
                            const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                            const convertedValue = DRQBuilder.convertRelativeDateForAPI(val, input.operator, input.type);
                            queries.push(DRQBuilder.exclude(condition, convertedValue, input.type, input.field_name));
                        });
                        if (queries.length > 0) {
                            // Combine all exclude filters with AND (all must be excluded)
                            subQueries.push(DRQBuilder.and(queries) as AnyDRQQuery);
                        }
                        return;
                    }

                    // For other operators: create separate queries and combine with OR
                    const queries: AnyDRQQuery[] = [];

                    values.forEach(val => {
                        const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                        const convertedValue = DRQBuilder.convertRelativeDateForAPI(val, input.operator, input.type);
                        queries.push(DRQBuilder.filter(condition, convertedValue, input.type, input.field_name));
                    });

                    if (queries.length > 0) {
                        // For contains/equals/starts_with/ends_with: combine with OR (any match)
                        subQueries.push(DRQBuilder.or(queries) as AnyDRQQuery);
                    }
                    return;
                }

                let value = DRQBuilder.convertRelativeDateForAPI(input.value, input.operator, input.type);
                let condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, value, input.type);
                let operator = input.operator;

                // Handle special case for equals/not_equal_to with empty values
                if ((input.operator === "equals" || input.operator === "not_equal_to") && (!input.value || input.value.trim() === "")) {
                    if (input.operator === "equals") {
                        // equals with empty value: field IS NULL OR field = ""
                        const nullFilter = DRQBuilder.filter(`${input.field_name}__isnull`, true, input.type, input.field_name);
                        const emptyFilter = DRQBuilder.filter(`${input.field_name}__exact`, "", input.type, input.field_name);
                        subQueries.push(DRQBuilder.or([nullFilter, emptyFilter]) as AnyDRQQuery);
                    } else {
                        // not_equal_to with empty value: field IS NOT NULL AND field != ""
                        const notNullFilter = DRQBuilder.filter(`${input.field_name}__isnull`, false, input.type, input.field_name);
                        const notEmptyFilter = DRQBuilder.exclude(`${input.field_name}__exact`, "", input.type, input.field_name);
                        subQueries.push(DRQBuilder.and([notNullFilter, notEmptyFilter]) as AnyDRQQuery);
                    }
                    return;
                }

                // Handle special case for temporal lookups that return objects (like year-month)
                if (typeof value === "object" && value !== null && "year" in value && "month" in value) {
                    // Create two separate filters for year and month
                    const yearCondition = `${input.field_name}__year`;
                    const monthCondition = `${input.field_name}__month`;

                    if (DRQBuilder.shouldUseExclude(input.operator)) {
                        subQueries.push(DRQBuilder.exclude(yearCondition, value.year, input.type, input.field_name));
                        subQueries.push(DRQBuilder.exclude(monthCondition, value.month, input.type, input.field_name));
                    } else {
                        subQueries.push(DRQBuilder.filter(yearCondition, value.year, input.type, input.field_name));
                        subQueries.push(DRQBuilder.filter(monthCondition, value.month, input.type, input.field_name));
                    }
                    return;
                }

                if (DRQBuilder.shouldUseExclude(operator)) {
                    subQueries.push(DRQBuilder.exclude(condition, value, input.type, input.field_name));
                } else {
                    subQueries.push(DRQBuilder.filter(condition, value, input.type, input.field_name));
                }
            }
        });

        return DRQBuilder.and(subQueries);
    }

    /**
     * Build DRQ query from filter inputs (for storage, keeps relative dates)
     */
    static buildFromFilterInputs(filterInputs: DRQFilterInput[]): AnyDRQQuery | null {
        const subQueries: AnyDRQQuery[] = [];

        filterInputs.forEach(input => {
            if (
                input.value ||
                input.operator === "is_empty" ||
                input.operator === "is_not_empty" ||
                ((input.operator === "equals" || input.operator === "not_equal_to") && (!input.value || input.value.trim() === ""))
            ) {
                if (input.operator === "is_empty") {
                    subQueries.push(DRQBuilder.filter(`${input.field_name}__isnull`, input.value === "True", input.type, input.field_name));
                    return;
                }
                // Handle comma-separated values for contains, not_contains, equals, and not_equal_to operators
                if (
                    (input.operator === "contains" || input.operator === "not_contains" || input.operator === "equals" || input.operator === "not_equal_to") &&
                    typeof input.value === "string" &&
                    input.value.includes(",")
                ) {
                    const values = input.value
                        .split(",")
                        .map(v => v.trim())
                        .filter(v => v.length > 0);

                    // Special handling for not_equal_to: create separate exclude filters and combine with AND
                    if (input.operator === "not_equal_to") {
                        const queries: AnyDRQQuery[] = [];
                        values.forEach(val => {
                            const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                            const convertedValue = DRQBuilder.convertValue(val, input.operator, input.type);
                            queries.push(DRQBuilder.exclude(condition, convertedValue, input.type, input.field_name));
                        });
                        if (queries.length > 0) {
                            // Combine all exclude filters with AND (all must be excluded)
                            subQueries.push(DRQBuilder.and(queries) as AnyDRQQuery);
                        }
                        return;
                    }

                    // Special handling for not_contains: create separate exclude filters and combine with AND
                    if (input.operator === "not_contains") {
                        const queries: AnyDRQQuery[] = [];
                        values.forEach(val => {
                            const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                            const convertedValue = DRQBuilder.convertValue(val, input.operator, input.type);
                            queries.push(DRQBuilder.exclude(condition, convertedValue, input.type, input.field_name));
                        });
                        if (queries.length > 0) {
                            // Combine all exclude filters with AND (all must be excluded)
                            subQueries.push(DRQBuilder.and(queries) as AnyDRQQuery);
                        }
                        return;
                    }

                    // For other operators: create separate queries and combine with OR
                    const queries: AnyDRQQuery[] = [];

                    values.forEach(val => {
                        const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, val, input.type);
                        const convertedValue = DRQBuilder.convertValue(val, input.operator, input.type);
                        queries.push(DRQBuilder.filter(condition, convertedValue, input.type, input.field_name));
                    });

                    if (queries.length > 0) {
                        // For contains/equals: combine with OR (any match)
                        subQueries.push(DRQBuilder.or(queries) as AnyDRQQuery);
                    }
                    return;
                }

                const value = DRQBuilder.convertValue(input.value, input.operator, input.type);
                const condition = DRQBuilder.getConditionFromOperator(input.field_name, input.operator, value, input.type);

                // Handle special case for temporal lookups that return objects (like year-month)
                if (typeof value === "object" && value !== null && "year" in value && "month" in value) {
                    // Create two separate filters for year and month
                    const yearCondition = `${input.field_name}__year`;
                    const monthCondition = `${input.field_name}__month`;

                    if (DRQBuilder.shouldUseExclude(input.operator)) {
                        subQueries.push(DRQBuilder.exclude(yearCondition, value.year, input.type, input.field_name));
                        subQueries.push(DRQBuilder.exclude(monthCondition, value.month, input.type, input.field_name));
                    } else {
                        subQueries.push(DRQBuilder.filter(yearCondition, value.year, input.type, input.field_name));
                        subQueries.push(DRQBuilder.filter(monthCondition, value.month, input.type, input.field_name));
                    }
                    return;
                }

                if (DRQBuilder.shouldUseExclude(input.operator)) {
                    subQueries.push(DRQBuilder.exclude(condition, value, input.type, input.field_name));
                } else {
                    subQueries.push(DRQBuilder.filter(condition, value, input.type, input.field_name));
                }
            }
        });

        return DRQBuilder.and(subQueries);
    }

    /**
     * Build DRQ query from filter instances array (allows multiple filters with same field_name)
     */
    static buildFromFilterInstances(filterInstances: FilterInstance[]): AnyDRQQuery | {} {
        const filterInputs: DRQFilterInput[] = filterInstances.map(instance => ({
            field_name: instance.field_name,
            type: instance.type as any,
            value: instance.value,
            operator: instance.operator
        }));

        return DRQBuilder.buildFromFilterInputs(filterInputs) || {};
    }

    /**
     * Build DRQ query from filter instances array for API calls (converts relative dates)
     */
    static buildFromFilterInstancesForAPI(filterInstances: FilterInstance[]): AnyDRQQuery | {} {
        const filterInputs: DRQFilterInput[] = filterInstances.map(instance => ({
            field_name: instance.field_name,
            type: instance.type as any,
            value: instance.value,
            operator: instance.operator
        }));

        return DRQBuilder.buildFromFilterInputsForAPI(filterInputs) || {};
    }

    /**
     * Build DRQ query from filter state map
     */
    static buildFromFilterState(filterState: Map<string, { value: string; operator: string; type?: string }>): AnyDRQQuery | null {
        const filterInputs: DRQFilterInput[] = Array.from(filterState.entries()).map(([fieldName, data]) => ({
            field_name: fieldName,
            type: data.type as any,
            value: data.value,
            operator: data.operator
        }));

        return DRQBuilder.buildFromFilterInputs(filterInputs);
    }

    /**
     * Extract filter instances from DRQ query (preserves multiple filters with same field_name)
     */
    static extractFilterInstancesFromDRQ(drqQuery: AnyDRQQuery | {}): FilterInstance[] {
        const filterInstances: FilterInstance[] = [];

        if (!drqQuery) {
            return filterInstances;
        }

        const processFilter = (filter: DRQFilter | DRQExclude) => {
            console.log("operator", filter);
            const { fieldName, operator } = DRQBuilder.getOperatorFromCondition(filter._condition, filter._type);

            // If this is an exclude query, convert to the negative operator
            let finalOperator = operator;
            if (filter._query_class === "exclude") {
                switch (operator) {
                    case "contains":
                        finalOperator = "not_contains";
                        break;
                    case "equals":
                        finalOperator = "not_equal_to";
                        break;
                    default:
                        finalOperator = operator;
                }
            }

            // Convert value back to string format
            let valueStr = String(filter._value);
            if (Array.isArray(filter._value)) {
                valueStr = filter._value.join(", ");
            }
            if (typeof filter._value === "boolean") {
                valueStr = filter._value ? "True" : "False";
            }

            filterInstances.push({
                id: `${fieldName}_${Date.now()}_${Math.random()}`,
                field_name: fieldName,
                value: valueStr,
                operator: finalOperator,
                type: drqFilterTypeForApi(filter._condition, filter._type)
            });
        };

        // Special handler for OR queries to detect contains and equals patterns
        const processOrQuery = (orQuery: DRQOrQuery) => {
            // Check if this is an equals with empty value pattern: filter(__isnull=true) OR filter(__exact="")
            if (orQuery._sub_queries.length === 2) {
                const [firstQuery, secondQuery] = orQuery._sub_queries;

                if (firstQuery._query_class === "filter" && secondQuery._query_class === "filter") {
                    const firstFilter = firstQuery as DRQFilter;
                    const secondFilter = secondQuery as DRQFilter;

                    const { fieldName: firstFieldName, operator: firstOperator } = DRQBuilder.getOperatorFromCondition(firstFilter._condition, firstFilter._type);
                    const { fieldName: secondFieldName, operator: secondOperator } = DRQBuilder.getOperatorFromCondition(secondFilter._condition, secondFilter._type);

                    if (firstOperator === "is_empty" && firstFilter._value === true && secondOperator === "equals" && secondFilter._value === "" && firstFieldName === secondFieldName) {
                        // This is an equals with empty value pattern
                        filterInstances.push({
                            id: `${firstFieldName}_${Date.now()}_${Math.random()}`,
                            field_name: firstFieldName,
                            value: "",
                            operator: "equals",
                            type: drqFilterTypeForApi(firstFilter._condition, firstFilter._type)
                        });
                        return;
                    }
                }
            }

            const containsFilters: FilterInstance[] = [];
            const equalsFilters: FilterInstance[] = [];
            const startsWithFilters: FilterInstance[] = [];
            const endsWithFilters: FilterInstance[] = [];
            const otherQueries: AnyDRQQuery[] = [];

            // Check if all sub-queries are "contains", "equals", "starts_with", or "ends_with" filters on the same field
            orQuery._sub_queries.forEach(subQuery => {
                if (subQuery._query_class === "filter") {
                    const filter = subQuery as DRQFilter;
                    console.log("operator", filter);
                    const { fieldName, operator } = DRQBuilder.getOperatorFromCondition(filter._condition, filter._type);

                    if (operator === "contains") {
                        let valueStr = String(filter._value);
                        const ft = drqFilterTypeForApi(filter._condition, filter._type);
                        containsFilters.push({
                            id: `${fieldName}_${Date.now()}_${Math.random()}`,
                            field_name: fieldName,
                            value: valueStr,
                            operator: "contains",
                            type: ft
                        });
                    } else if (operator === "equals") {
                        let valueStr = String(filter._value);
                        const ft = drqFilterTypeForApi(filter._condition, filter._type);
                        equalsFilters.push({
                            id: `${fieldName}_${Date.now()}_${Math.random()}`,
                            field_name: fieldName,
                            value: valueStr,
                            operator: "equals",
                            type: ft
                        });
                    } else if (operator === "starts_with") {
                        let valueStr = String(filter._value);
                        const ft = drqFilterTypeForApi(filter._condition, filter._type);
                        startsWithFilters.push({
                            id: `${fieldName}_${Date.now()}_${Math.random()}`,
                            field_name: fieldName,
                            value: valueStr,
                            operator: "starts_with",
                            type: ft
                        });
                    } else if (operator === "ends_with") {
                        let valueStr = String(filter._value);
                        const ft = drqFilterTypeForApi(filter._condition, filter._type);
                        endsWithFilters.push({
                            id: `${fieldName}_${Date.now()}_${Math.random()}`,
                            field_name: fieldName,
                            value: valueStr,
                            operator: "ends_with",
                            type: ft
                        });
                    } else {
                        otherQueries.push(subQuery as AnyDRQQuery);
                    }
                } else {
                    otherQueries.push(subQuery as AnyDRQQuery);
                }
            });

            // If we have multiple contains filters on the same field, combine them
            if (containsFilters.length > 1) {
                const groupedByField: { [key: string]: FilterInstance[] } = {};
                containsFilters.forEach(filter => {
                    if (!groupedByField[filter.field_name]) {
                        groupedByField[filter.field_name] = [];
                    }
                    groupedByField[filter.field_name].push(filter);
                });

                Object.values(groupedByField).forEach(group => {
                    if (group.length > 1) {
                        // Combine into a single filter with comma-separated values
                        const combinedValues = group.map(f => f.value).join(", ");
                        filterInstances.push({
                            id: `${group[0].field_name}_${Date.now()}_${Math.random()}`,
                            field_name: group[0].field_name,
                            value: combinedValues,
                            operator: "contains",
                            type: group[0].type
                        });
                    } else {
                        filterInstances.push(group[0]);
                    }
                });
            } else {
                // Add individual contains filters if any
                filterInstances.push(...containsFilters);
            }

            // If we have multiple equals filters on the same field, combine them
            if (equalsFilters.length > 1) {
                const groupedByField: { [key: string]: FilterInstance[] } = {};
                equalsFilters.forEach(filter => {
                    if (!groupedByField[filter.field_name]) {
                        groupedByField[filter.field_name] = [];
                    }
                    groupedByField[filter.field_name].push(filter);
                });

                Object.values(groupedByField).forEach(group => {
                    if (group.length > 1) {
                        // Combine into a single filter with comma-separated values
                        const combinedValues = group.map(f => f.value).join(", ");
                        filterInstances.push({
                            id: `${group[0].field_name}_${Date.now()}_${Math.random()}`,
                            field_name: group[0].field_name,
                            value: combinedValues,
                            operator: "equals",
                            type: group[0].type
                        });
                    } else {
                        filterInstances.push(group[0]);
                    }
                });
            } else {
                // Add individual equals filters if any
                filterInstances.push(...equalsFilters);
            }

            // If we have multiple starts_with filters on the same field, combine them
            if (startsWithFilters.length > 1) {
                const groupedByField: { [key: string]: FilterInstance[] } = {};
                startsWithFilters.forEach(filter => {
                    if (!groupedByField[filter.field_name]) {
                        groupedByField[filter.field_name] = [];
                    }
                    groupedByField[filter.field_name].push(filter);
                });

                Object.values(groupedByField).forEach(group => {
                    if (group.length > 1) {
                        // Combine into a single filter with comma-separated values
                        const combinedValues = group.map(f => f.value).join(", ");
                        filterInstances.push({
                            id: `${group[0].field_name}_${Date.now()}_${Math.random()}`,
                            field_name: group[0].field_name,
                            value: combinedValues,
                            operator: "starts_with",
                            type: group[0].type
                        });
                    } else {
                        filterInstances.push(group[0]);
                    }
                });
            } else {
                // Add individual starts_with filters if any
                filterInstances.push(...startsWithFilters);
            }

            // If we have multiple ends_with filters on the same field, combine them
            if (endsWithFilters.length > 1) {
                const groupedByField: { [key: string]: FilterInstance[] } = {};
                endsWithFilters.forEach(filter => {
                    if (!groupedByField[filter.field_name]) {
                        groupedByField[filter.field_name] = [];
                    }
                    groupedByField[filter.field_name].push(filter);
                });

                Object.values(groupedByField).forEach(group => {
                    if (group.length > 1) {
                        // Combine into a single filter with comma-separated values
                        const combinedValues = group.map(f => f.value).join(", ");
                        filterInstances.push({
                            id: `${group[0].field_name}_${Date.now()}_${Math.random()}`,
                            field_name: group[0].field_name,
                            value: combinedValues,
                            operator: "ends_with",
                            type: group[0].type
                        });
                    } else {
                        filterInstances.push(group[0]);
                    }
                });
            } else {
                // Add individual ends_with filters if any
                filterInstances.push(...endsWithFilters);
            }

            // Process other queries normally
            otherQueries.forEach(subQuery => {
                processQuery(subQuery as AnyDRQQuery);
            });
        };

        // Special handler for composite queries to detect not_equal_to with empty value patterns
        const processCompositeQuery = (compositeQuery: DRQCompositeQuery) => {
            // Check if this is a not_equal_to with empty value pattern: filter(__isnull=false) AND exclude(__exact="")
            if (compositeQuery._sub_queries.length === 2) {
                const [firstQuery, secondQuery] = compositeQuery._sub_queries;

                if (firstQuery._query_class === "filter" && secondQuery._query_class === "exclude") {
                    const firstFilter = firstQuery as DRQFilter;
                    const secondExclude = secondQuery as DRQExclude;

                    const { fieldName: firstFieldName, operator: firstOperator } = DRQBuilder.getOperatorFromCondition(firstFilter._condition, firstFilter._type);
                    const { fieldName: secondFieldName, operator: secondOperator } = DRQBuilder.getOperatorFromCondition(secondExclude._condition, secondExclude._type);

                    if (firstOperator === "is_empty" && firstFilter._value === false && secondOperator === "equals" && secondExclude._value === "" && firstFieldName === secondFieldName) {
                        // This is a not_equal_to with empty value pattern
                        filterInstances.push({
                            id: `${firstFieldName}_${Date.now()}_${Math.random()}`,
                            field_name: firstFieldName,
                            value: "",
                            operator: "not_equal_to",
                            type: firstFilter._type
                        });
                        return;
                    }
                }
            }

            // Check if this is a pattern of multiple exclude filters on the same field (not_equal_to or not_contains with comma-separated values)
            const excludeFilters: DRQExclude[] = [];
            const otherQueries: AnyDRQQuery[] = [];

            compositeQuery._sub_queries.forEach(subQuery => {
                if (subQuery._query_class === "exclude") {
                    excludeFilters.push(subQuery as DRQExclude);
                } else {
                    otherQueries.push(subQuery as AnyDRQQuery);
                }
            });

            // If we have multiple exclude filters, check if they're on the same field
            if (excludeFilters.length > 1) {
                const groupedByField: { [key: string]: DRQExclude[] } = {};
                excludeFilters.forEach(exclude => {
                    const { fieldName } = DRQBuilder.getOperatorFromCondition(exclude._condition, exclude._type);
                    if (!groupedByField[fieldName]) {
                        groupedByField[fieldName] = [];
                    }
                    groupedByField[fieldName].push(exclude);
                });

                // Process queries in their original order to preserve field order
                const processedFields = new Set<string>();
                compositeQuery._sub_queries.forEach(subQuery => {
                    if (subQuery._query_class === "exclude") {
                        const exclude = subQuery as DRQExclude;
                        const { fieldName } = DRQBuilder.getOperatorFromCondition(exclude._condition, exclude._type);

                        if (!processedFields.has(fieldName)) {
                            processedFields.add(fieldName);
                            const fieldExcludes = groupedByField[fieldName];

                            if (fieldExcludes.length > 1) {
                                // Get the operator type from the first exclude filter
                                const { operator } = DRQBuilder.getOperatorFromCondition(fieldExcludes[0]._condition, fieldExcludes[0]._type);
                                let finalOperator = "not_equal_to";

                                if (operator === "contains") {
                                    finalOperator = "not_contains";
                                } else if (operator === "equals") {
                                    finalOperator = "not_equal_to";
                                }

                                // Combine all values with commas
                                const values = fieldExcludes.map(exclude => String(exclude._value));
                                const combinedValue = values.join(", ");

                                filterInstances.push({
                                    id: `${fieldName}_${Date.now()}_${Math.random()}`,
                                    field_name: fieldName,
                                    value: combinedValue,
                                    operator: finalOperator,
                                    type: drqFilterTypeForApi(fieldExcludes[0]._condition, fieldExcludes[0]._type)
                                });
                            } else {
                                // Single exclude filter, process normally
                                processFilter(fieldExcludes[0]);
                            }
                        }
                    } else {
                        // Process non-exclude queries normally
                        processQuery(subQuery as AnyDRQQuery);
                    }
                });
            } else {
                // Process all sub-queries normally in their original order
                compositeQuery._sub_queries.forEach(subQuery => {
                    if (subQuery._query_class === "compositequery") {
                        // Recursively process nested compositequeries
                        processCompositeQuery(subQuery as DRQCompositeQuery);
                    } else {
                        processQuery(subQuery as AnyDRQQuery);
                    }
                });
            }
        };

        // Recursive function to handle nested queries
        const processQuery = (query: AnyDRQQuery) => {
            if (query._query_class === "filter" || query._query_class === "exclude") {
                processFilter(query as DRQFilter | DRQExclude);
            } else if (query._query_class === "orquery") {
                processOrQuery(query as DRQOrQuery);
            } else if (query._query_class === "compositequery") {
                // Process compositequery
                const composite = query as DRQCompositeQuery;
                processCompositeQuery(composite);
            }
        };

        // Handle different query types
        if (drqQuery && "_query_class" in drqQuery) {
            processQuery(drqQuery as AnyDRQQuery);
        }

        return filterInstances;
    }

    /**
     * Extract filter state from DRQ query
     * This is used when loading saved views
     */
    static extractFilterStateFromDRQ(drqQuery: AnyDRQQuery | {}): Map<string, { value: string; operator: string; type?: string }> {
        const filterState = new Map<string, { value: string; operator: string; type?: string }>();

        if (!drqQuery) {
            return filterState;
        }

        const processFilter = (filter: DRQFilter | DRQExclude) => {
            console.log("operator", filter);
            const { fieldName, operator } = DRQBuilder.getOperatorFromCondition(filter._condition, filter._type);

            // Handle special case for is_null operators
            let finalOperator = operator;
            if (operator === "is_empty" && filter._value === false) {
                finalOperator = "is_not_empty";
            }

            // If this is an exclude query, convert to the negative operator
            if (filter._query_class === "exclude") {
                switch (operator) {
                    case "contains":
                        finalOperator = "not_contains";
                        break;
                    case "equals":
                        finalOperator = "not_equal_to";
                        break;
                    default:
                        finalOperator = operator;
                }
            }

            // Convert value back to string format
            let valueStr = String(filter._value);
            if (Array.isArray(filter._value)) {
                valueStr = filter._value.join(", ");
            }
            if (typeof filter._value === "boolean") {
                valueStr = filter._value ? "True" : "False";
            }

            filterState.set(fieldName, {
                value: valueStr,
                operator: finalOperator,
                type: filter._type
            });
        };

        // Recursive function to handle nested queries
        const processQuery = (query: AnyDRQQuery) => {
            if (query._query_class === "filter" || query._query_class === "exclude") {
                processFilter(query as DRQFilter | DRQExclude);
            } else if (query._query_class === "compositequery" || query._query_class === "orquery") {
                const compositeQuery = query as DRQCompositeQuery | DRQOrQuery;
                compositeQuery._sub_queries.forEach(subQuery => {
                    processQuery(subQuery as AnyDRQQuery);
                });
            }
            // orderby queries don't contain filters, so we can ignore them
        };

        // Handle different query types
        if (drqQuery && "_query_class" in drqQuery) {
            processQuery(drqQuery as AnyDRQQuery);
        }

        return filterState;
    }

    /**
     * Build DRQ query from filter instances array with a custom logic expression
     * Logic expression examples : "1 AND 2", "1 AND (2 OR 3)"
     * Indexes are 1-based references to the order of filterInstances
     */
    static buildFromFilterInstancesWithLogic(filterInstances: FilterInstance[], logicExpression?: string): AnyDRQQuery | {} {
        // Si pas d'expression ou vide, on retombe sur la logique par défaut
        if (!logicExpression || logicExpression.trim() === "") {
            return DRQBuilder.buildFromFilterInstances(filterInstances);
        }

        // Générer les requêtes unitaires pour chaque filtre
        const unitQueries: AnyDRQQuery[] = filterInstances.map(instance => {
            return DRQBuilder.buildFromFilterInputs([
                {
                    field_name: instance.field_name,
                    type: instance.type as any,
                    value: instance.value,
                    operator: instance.operator
                }
            ]) as AnyDRQQuery;
        });

        // Tokenisation
        const tokens = logicExpression
            .replace(/\(/g, " ( ")
            .replace(/\)/g, " ) ")
            .split(/\s+/)
            .filter(t => t.length > 0)
            .map(t => t.toUpperCase());

        // Shunting-yard pour gérer la précédence (AND > OR)
        const outputStack: (number | string)[] = [];
        const operatorStack: string[] = [];
        const precedence: Record<string, number> = { AND: 2, OR: 1 };

        for (const token of tokens) {
            if (/^\d+$/.test(token)) {
                outputStack.push(parseInt(token, 10));
            } else if (token === "AND" || token === "OR") {
                while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== "(" && precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]) {
                    outputStack.push(operatorStack.pop() as string);
                }
                operatorStack.push(token);
            } else if (token === "(") {
                operatorStack.push(token);
            } else if (token === ")") {
                while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== "(") {
                    outputStack.push(operatorStack.pop() as string);
                }
                operatorStack.pop(); // Remove "("
            }
        }
        while (operatorStack.length > 0) {
            outputStack.push(operatorStack.pop() as string);
        }

        // Évaluation du postfix
        const evalStack: AnyDRQQuery[] = [];
        for (const token of outputStack) {
            if (typeof token === "number") {
                const idx = token - 1;
                if (idx >= 0 && idx < unitQueries.length) {
                    evalStack.push(unitQueries[idx]);
                }
            } else if (token === "AND" || token === "OR") {
                const right = evalStack.pop();
                const left = evalStack.pop();
                if (!left || !right) continue;
                if (token === "AND") {
                    evalStack.push(DRQBuilder.and([left, right]) as AnyDRQQuery);
                } else {
                    evalStack.push(DRQBuilder.or([left, right]) as AnyDRQQuery);
                }
            }
        }

        return evalStack[0] || {};
    }

    /**
     * Returns true if the DRQ is a flat OR query (top-level orquery whose sub-queries are all filter/exclude).
     * Complex nested structures (e.g. OR(AND(f1,f2), f3)) return false.
     */
    static isDRQFlatOrQuery(drqQuery: AnyDRQQuery | {}): boolean {
        if (!drqQuery || !("_query_class" in drqQuery)) return false;
        const q = drqQuery as AnyDRQQuery;
        if (q._query_class !== "orquery") return false;
        const oq = q as DRQOrQuery;
        return oq._sub_queries.length >= 2 && oq._sub_queries.every(sq => sq._query_class === "filter" || sq._query_class === "exclude");
    }

    /**
     * Builds a logic expression string "1 OR 2 OR ... N" for a flat OR query with `count` conditions.
     */
    static reconstructFlatOrLogicExpression(count: number): string {
        if (count < 2) return "";
        return Array.from({ length: count }, (_, i) => String(i + 1)).join(" OR ");
    }

    /**
     * Build DRQ query from filter instances array for API calls with custom logic
     */
    static buildFromFilterInstancesForAPIWithLogic(filterInstances: FilterInstance[], logicExpression?: string): AnyDRQQuery | {} {
        // Même logique mais on utilise la conversion API avant de combiner
        if (!logicExpression || logicExpression.trim() === "") {
            return DRQBuilder.buildFromFilterInstancesForAPI(filterInstances);
        }

        const unitQueries: AnyDRQQuery[] = filterInstances.map(instance => {
            return DRQBuilder.buildFromFilterInputsForAPI([
                {
                    field_name: instance.field_name,
                    type: instance.type as any,
                    value: instance.value,
                    operator: instance.operator
                }
            ]) as AnyDRQQuery;
        });

        // Reuse same parsing/eval code
        const tokens = logicExpression
            .replace(/\(/g, " ( ")
            .replace(/\)/g, " ) ")
            .split(/\s+/)
            .filter(t => t.length > 0)
            .map(t => t.toUpperCase());

        const outputStack: (number | string)[] = [];
        const operatorStack: string[] = [];
        const precedence: Record<string, number> = { AND: 2, OR: 1 };

        for (const token of tokens) {
            if (/^\d+$/.test(token)) {
                outputStack.push(parseInt(token, 10));
            } else if (token === "AND" || token === "OR") {
                while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== "(" && precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]) {
                    outputStack.push(operatorStack.pop() as string);
                }
                operatorStack.push(token);
            } else if (token === "(") {
                operatorStack.push(token);
            } else if (token === ")") {
                while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== "(") {
                    outputStack.push(operatorStack.pop() as string);
                }
                operatorStack.pop();
            }
        }
        while (operatorStack.length > 0) {
            outputStack.push(operatorStack.pop() as string);
        }

        const evalStack: AnyDRQQuery[] = [];
        for (const token of outputStack) {
            if (typeof token === "number") {
                const idx = token - 1;
                if (idx >= 0 && idx < unitQueries.length) {
                    evalStack.push(unitQueries[idx]);
                }
            } else if (token === "AND" || token === "OR") {
                const right = evalStack.pop();
                const left = evalStack.pop();
                if (!left || !right) continue;
                if (token === "AND") {
                    evalStack.push(DRQBuilder.and([left, right]) as AnyDRQQuery);
                } else {
                    evalStack.push(DRQBuilder.or([left, right]) as AnyDRQQuery);
                }
            }
        }

        return evalStack[0] || {};
    }
}

/**
 * Utility to serialize DRQ query to JSON string for API requests
 */
export function serializeDRQQuery(query: AnyDRQQuery | {}): string | null {
    if (Object.keys(query).length === 0) return null;
    return JSON.stringify(query);
}

/**
 * Utility to validate DRQ query structure
 */
export function validateDRQQuery(query: any): boolean {
    if (!query || typeof query !== "object") return false;
    if (!query._query_class) return false;

    const validClasses = ["filter", "exclude", "compositequery", "orquery", "orderby"];
    return validClasses.includes(query._query_class);
}

/**
 * Convertit une requête DRQ en phrase en anglais lisible par un humain
 */
export function drqToEnglish(query: AnyDRQQuery | {}, options?: { fieldLabels?: Record<string, string> }): string {
    if (!query || Object.keys(query).length === 0) return "";

    const fieldLabels = options?.fieldLabels || {};

    const getLabel = (fieldName: string): string => {
        return fieldLabels[fieldName] || fieldName;
    };

    const formatDateOrDateTime = (val: string): string => {
        if (typeof val !== "string") return String(val);
        // Simplify ISO strings for readability
        let out = val.replace("T", " ");
        out = out.replace(/\.(\d{3})Z$/, "Z");
        out = out.replace(/Z$/, " UTC");
        return out;
    };

    const formatValue = (value: any, type?: string, condition?: string): string => {
        if (Array.isArray(value)) {
            if (type === "date-range" || type === "datetime-range" || (condition && condition.endsWith("__range"))) {
                const [from, to] = value;
                return `from ${formatDateOrDateTime(String(from))} to ${formatDateOrDateTime(String(to))}`;
            }
            return value.map(v => String(v)).join(" or ");
        }
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }
        if (type === "datetime-range" || type === "date-range") {
            return formatDateOrDateTime(String(value));
        }
        return String(value);
    };

    const describeFilter = (filter: DRQFilter | DRQExclude): string => {
        const { fieldName, operator } = DRQBuilder.getOperatorFromCondition(filter._condition, filter._type);
        const isExclude = filter._query_class === "exclude";

        let finalOperator = operator;
        if (isExclude) {
            if (operator === "contains") finalOperator = "not_contains";
            else if (operator === "equals") finalOperator = "not_equal_to";
        }

        // Special cases for isnull patterns
        if (filter._condition.endsWith("__isnull")) {
            const isEmpty = Boolean(filter._value);
            return `${getLabel(fieldName)} ${isEmpty ? "is empty" : "is not empty"}`;
        }

        // Range detection
        const isRange = filter._condition.endsWith("__range");

        const valStr = formatValue(filter._value, filter._type, filter._condition);

        switch (finalOperator) {
            case "contains":
                return `${getLabel(fieldName)} contains "${valStr}"`;
            case "not_contains":
                return `${getLabel(fieldName)} does not contain "${valStr}"`;
            case "starts_with":
                return `${getLabel(fieldName)} starts with "${valStr}"`;
            case "ends_with":
                return `${getLabel(fieldName)} ends with "${valStr}"`;
            case "lt":
                return `${getLabel(fieldName)} is less than ${valStr}`;
            case "lte":
                return `${getLabel(fieldName)} is less than or equal to ${valStr}`;
            case "gt":
                return `${getLabel(fieldName)} is greater than ${valStr}`;
            case "gte":
                return `${getLabel(fieldName)} is greater than or equal to ${valStr}`;
            case "not_equal_to":
                if (filter._type === "checkbox") {
                    return `${getLabel(fieldName)} is not ${valStr}`;
                }
                if (String(filter._value) === "") {
                    return `${getLabel(fieldName)} is not empty`;
                }
                return `${getLabel(fieldName)} is not "${valStr}"`;
            case "equals":
            default:
                if (filter._type === "checkbox") {
                    return `${getLabel(fieldName)} is ${valStr}`;
                }
                if (isRange) {
                    return `${getLabel(fieldName)} is between ${valStr}`;
                }
                if (String(filter._value) === "") {
                    return `${getLabel(fieldName)} is empty`;
                }
                return `${getLabel(fieldName)} is "${valStr}"`;
        }
    };

    // Try to compress simple OR groups of same field/operator into "A is X or Y"
    const describeOrQuery = (orQuery: DRQOrQuery): string => {
        // Detect equals-empty special case handled earlier in DRQBuilder.extract*; we provide a generic OR join here
        // If we can detect a simple group of same field/op, compress
        type GroupKey = { field: string; op: string; type?: string };
        const groups: Record<string, { key: GroupKey; values: string[]; template: (label: string, value: string) => string } | null> = {};
        const individual: string[] = [];

        const tryParseFilter = (q: AnyDRQQuery) => {
            if (q._query_class !== "filter" && q._query_class !== "exclude") return null;
            const f = q as DRQFilter | DRQExclude;
            const { fieldName, operator } = DRQBuilder.getOperatorFromCondition(f._condition, f._type);
            const isExclude = q._query_class === "exclude";
            let finalOperator = operator;
            if (isExclude) {
                if (operator === "contains") finalOperator = "not_contains";
                else if (operator === "equals") finalOperator = "not_equal_to";
            }
            // Only compress for these ops
            if (["contains", "equals", "starts_with", "ends_with"].includes(finalOperator) && !f._condition.endsWith("__range")) {
                const val = formatValue(f._value, f._type, f._condition);
                const key = `${fieldName}|${finalOperator}|${f._type || ""}`;
                const templateMap: Record<string, (label: string, value: string) => string> = {
                    contains: (l, v) => `${l} contains "${v}"`,
                    equals: (l, v) => `${l} is "${v}"`,
                    starts_with: (l, v) => `${l} starts with "${v}"`,
                    ends_with: (l, v) => `${l} ends with "${v}"`
                };
                if (!groups[key]) {
                    groups[key] = { key: { field: fieldName, op: finalOperator, type: f._type }, values: [], template: templateMap[finalOperator] };
                }
                groups[key]!.values.push(val);
                return true;
            }
            return null;
        };

        orQuery._sub_queries.forEach(q => {
            if (!tryParseFilter(q as AnyDRQQuery)) {
                individual.push(describe(q as AnyDRQQuery));
            }
        });

        const compressed: string[] = Object.values(groups)
            .filter(Boolean)
            .map(g => {
                const label = getLabel(g!.key.field);
                const joined = g!.values.join(" or ");
                return g!.template(label, joined);
            });

        const all = [...compressed, ...individual];
        return all.join(" or ");
    };

    const describeCompositeQuery = (cq: DRQCompositeQuery): string => {
        // Try to compress AND of multiple not_equal_to/not_contains on the same field to "is not one of"
        const excludes = cq._sub_queries.filter(sq => sq._query_class === "exclude") as DRQExclude[];
        const others = cq._sub_queries.filter(sq => sq._query_class !== "exclude") as AnyDRQQuery[];

        if (excludes.length > 1) {
            const byField: Record<string, DRQExclude[]> = {};
            excludes.forEach(ex => {
                const { fieldName } = DRQBuilder.getOperatorFromCondition(ex._condition, ex._type);
                byField[fieldName] = byField[fieldName] || [];
                byField[fieldName].push(ex);
            });

            const compressed: string[] = [];
            Object.entries(byField).forEach(([fieldName, list]) => {
                if (list.length > 1) {
                    const values = list.map(ex => formatValue(ex._value, ex._type, ex._condition));
                    compressed.push(`${getLabel(fieldName)} is not one of ${values.map(v => `"${v}"`).join(", ")}`);
                } else {
                    // Single exclude -> normal description
                    compressed.push(describeFilter(list[0]));
                }
            });

            const otherParts = others.map(q => describe(q));
            const all = [...compressed, ...otherParts];
            return all.join(" and ");
        }

        // Default: join all with AND
        return cq._sub_queries.map(q => describe(q as AnyDRQQuery)).join(" and ");
    };

    const describeOrderBy = (ob: DRQOrderBy): string => {
        if (!ob._order_by || ob._order_by.length === 0) return "";
        const parts = ob._order_by.map(f => {
            const desc = f.startsWith("-");
            const field = desc ? f.slice(1) : f;
            return `${getLabel(field)} ${desc ? "descending" : "ascending"}`;
        });
        return `ordered by ${parts.join(", ")}`;
    };

    const describe = (q: AnyDRQQuery): string => {
        switch (q._query_class) {
            case "filter":
            case "exclude":
                return describeFilter(q as DRQFilter | DRQExclude);
            case "orquery":
                return describeOrQuery(q as DRQOrQuery);
            case "compositequery":
                return describeCompositeQuery(q as DRQCompositeQuery);
            case "orderby":
                return describeOrderBy(q as DRQOrderBy);
            default:
                return "";
        }
    };

    return describe(query as AnyDRQQuery);
}
